import LZString from 'lz-string';
import mixpanel from 'mixpanel-browser';
import { useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { PageNavTrackingEvents } from '@revelio/core';
import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import {
  getPrimaryFilterIdForScreenerV2Page,
  isScreenerV2Page,
} from '../../utils/routing-helpers';
import type { DefaultSelectionOverride } from '../contexts/defaultFilters';
import type { CustomSkillTaxonomySelection } from '../definitions/skillTaxonomy';
import {
  createInitialFilterConsistencyState,
  type FilterConsistencyState,
  generatePageKey,
  getActiveFilterMap,
  handleReplaceFilters,
  NO_PRIMARY_FILTER_KEY,
} from '../engine/filterGroupConsistency';
import {
  type Filter,
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../types/filters';
import type { TaxonomyConfig } from '../types/taxonomy';
import { isTreeFilter } from '../utils/common';
import { validateParsedFilters } from '../utils/validate-parsed-filters';
import { validateTaxonomyFilters } from '../utils/validateTaxonomyFilters';
import { useTaxonomyVersionToast } from './useTaxonomyVersionToast';

export type DeserializeFilterFromUrlResult = {
  filterFromUrl: FilterConsistencyState | undefined;
  /** True when `filters` is present but decompress/parse fails (malformed share link). */
  urlFiltersParseFailed: boolean;
};

export const useDeserializeFilterFromUrl = ({
  pageId: pageIdFromProps,
  primaryFilterId: pagePrimaryFilterId,
  primaryFilterSelectionLimit,
  taxonomyConfig,
  defaultSelections,
  hydrateFromUrl = true,
}: {
  pageId: string;
  primaryFilterId?: PrimaryFilterId;
  primaryFilterSelectionLimit?: number;
  taxonomyConfig?: TaxonomyConfig;
  defaultSelections?: DefaultSelectionOverride;
  /** When false, skip reading `filters` / `filterGroups` from the URL (e.g. isolated chart preview). */
  hydrateFromUrl?: boolean;

  /** If we want to version based on the specific taxonomy selection, and not always the
   * default taxonomy, we can pass getSpecificRoleTaxonomyVersion to validateRoleFilters
   * or getSpecificSkillTaxonomyVersion to validateSkillFilters
   * and check the specific taxonomy version. */
  getSpecificRoleTaxonomyVersion?: (
    taxonomySelection: CustomRoleTaxonomySelection
  ) => string | undefined;
  getSpecificSkillTaxonomyVersion?: (
    taxonomySelection: CustomSkillTaxonomySelection
  ) => string | undefined;
}): DeserializeFilterFromUrlResult => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const showTaxonomyVersionToast = useTaxonomyVersionToast();
  const roleTaxonomyVersion = taxonomyConfig?.roleTaxonomyVersion;
  const skillTaxonomyVersion = taxonomyConfig?.skillTaxonomyVersion;
  const filtersSearchValue = searchParams.get('filters');
  const filterGroupsSearchValue = searchParams.get('filterGroups');

  // Ref-guarded because the memo below recomputes when taxonomy versions load —
  // a deeplink arrival should be recorded once, and only after a successful parse.
  const hasTrackedDeeplink = useRef(false);
  const trackDeeplink = (deeplinkParam: 'filterGroups' | 'filters') => {
    if (hasTrackedDeeplink.current) return;
    hasTrackedDeeplink.current = true;
    mixpanel.track(PageNavTrackingEvents.NAVIGATION_INITIATED, {
      navigation_source: 'deeplink',
      deeplink_param: deeplinkParam,
      path: pathname,
    });
  };

  // taxonomyConfig from FilterProvider is a new object each render; role/skill version strings are the real deps.
  // biome-ignore lint/correctness/useExhaustiveDependencies: taxonomyConfig fields are fully covered by roleTaxonomyVersion and skillTaxonomyVersion
  return useMemo(() => {
    type FilterGroupsOutcome =
      | { kind: 'absent' }
      | { kind: 'ok'; state: FilterConsistencyState }
      | { kind: 'invalid' };

    if (!hydrateFromUrl) {
      return {
        filterFromUrl: undefined,
        urlFiltersParseFailed: false,
      };
    }

    const deserializeFromFilterGroups = (): FilterGroupsOutcome => {
      const filterGroupsFromUrl = filterGroupsSearchValue;
      if (!filterGroupsFromUrl) return { kind: 'absent' };

      const filterGroupsDeserialized =
        LZString.decompressFromEncodedURIComponent(filterGroupsFromUrl);
      if (!filterGroupsDeserialized) return { kind: 'invalid' };

      let filterGroups: [string, [FilterId, Filter][]][];
      try {
        const parsed: unknown = JSON.parse(filterGroupsDeserialized);
        const groups = parseDecodedFilterGroupsPayload(parsed);
        if (groups === null) return { kind: 'invalid' };
        filterGroups = groups;
      } catch {
        return { kind: 'invalid' };
      }

      try {
        const primaryGroupIndex = (() => {
          if (pagePrimaryFilterId) {
            return filterGroups.findIndex(([, filterPairs]) =>
              filterPairs.some(
                ([filterId, filter]) =>
                  filterId === pagePrimaryFilterId &&
                  isTreeFilter(filter) &&
                  filter.isPrimary === true
              )
            );
          }

          const index = filterGroups.findIndex(([, filterPairs]) =>
            filterPairs.some(
              ([_, filter]) => isTreeFilter(filter) && filter.isPrimary === true
            )
          );
          return index >= 0 ? index : 0;
        })();

        const primaryFilterGroup = filterGroups?.[primaryGroupIndex];
        if (!primaryFilterGroup) return { kind: 'invalid' };

        const initialFilterState = createFilterState({
          pageId: pageIdFromProps || pathname.split('/').pop() || '',
          pathname,
          filterPairs: primaryFilterGroup[1],
          taxonomyConfig,
          defaultSelections,
          primaryFilterId: pagePrimaryFilterId,
          primaryFilterSelectionLimit,
        });

        const newGroupMap = new Map(initialFilterState.groupMap);
        for (let i = 0; i < filterGroups.length; i++) {
          if (i === primaryGroupIndex) continue;

          const additionalFilterGroup = filterGroups[i];
          if (!additionalFilterGroup) continue;

          const additionalFilterGroupMap = validateParsedFilters(
            additionalFilterGroup[1],
            {
              defaultFilterMap: new Map(),
              primaryFilterId: undefined,
              primaryFilterLimit: primaryFilterSelectionLimit || 5,
            }
          );
          newGroupMap.set(additionalFilterGroup[0], additionalFilterGroupMap);
        }
        return {
          kind: 'ok',
          state: {
            ...initialFilterState,
            groupMap: newGroupMap,
          },
        };
      } catch (error) {
        console.error('Failed to parse filterGroups from URL:', error);
        return { kind: 'invalid' };
      }
    };

    const filterGroupsOutcome = deserializeFromFilterGroups();
    if (filterGroupsOutcome.kind === 'ok') {
      trackDeeplink('filterGroups');
      return {
        filterFromUrl: filterGroupsOutcome.state,
        urlFiltersParseFailed: false,
      };
    }

    const filtersFromUrl = filtersSearchValue;
    if (filterGroupsOutcome.kind === 'invalid' && !filtersFromUrl) {
      return { filterFromUrl: undefined, urlFiltersParseFailed: true };
    }

    if (!filtersFromUrl) {
      return { filterFromUrl: undefined, urlFiltersParseFailed: false };
    }

    try {
      const urlStateSerialized =
        LZString.decompressFromEncodedURIComponent(filtersFromUrl);
      if (!urlStateSerialized) {
        return { filterFromUrl: undefined, urlFiltersParseFailed: true };
      }

      const parsed: [FilterId, Filter][] | undefined =
        JSON.parse(urlStateSerialized);

      if (!parsed) {
        return { filterFromUrl: undefined, urlFiltersParseFailed: true };
      }

      const filteredParsed = isScreenerV2Page(pageIdFromProps)
        ? parsed.filter(
            ([id]) =>
              id !== FilterId.ROLE_TAXONOMY && id !== FilterId.SKILL_TAXONOMY
          )
        : parsed;

      trackDeeplink('filters');

      return {
        filterFromUrl: createFilterState({
          pageId: pageIdFromProps || pathname.split('/').pop() || '',
          pathname,
          filterPairs: filteredParsed,
          taxonomyConfig,
          defaultSelections,
          primaryFilterId: pagePrimaryFilterId,
          primaryFilterSelectionLimit,
          showTaxonomyVersionToast,
        }),
        urlFiltersParseFailed: false,
      };
    } catch (error) {
      console.error('Failed to parse filters from URL:', error);
      return { filterFromUrl: undefined, urlFiltersParseFailed: true };
    }
  }, [
    pathname,
    pageIdFromProps,
    pagePrimaryFilterId,
    primaryFilterSelectionLimit,
    roleTaxonomyVersion,
    skillTaxonomyVersion,
    defaultSelections,
    showTaxonomyVersionToast,
    filtersSearchValue,
    filterGroupsSearchValue,
    hydrateFromUrl,
  ]);
};

function parseDecodedFilterGroupsPayload(
  parsed: unknown
): [string, [FilterId, Filter][]][] | null {
  if (!Array.isArray(parsed)) return null;
  const groups: [string, [FilterId, Filter][]][] = [];
  for (const entry of parsed) {
    if (!Array.isArray(entry) || entry.length !== 2) return null;
    const [groupKeyRaw, pairsRaw] = entry;
    if (typeof groupKeyRaw !== 'string') return null;
    if (!Array.isArray(pairsRaw)) return null;
    const pairs: [FilterId, Filter][] = [];
    for (const pair of pairsRaw) {
      if (!Array.isArray(pair) || pair.length !== 2) return null;
      const [idRaw, filterRaw] = pair;
      if (!isFilterIdValue(idRaw)) return null;
      if (!isFilterValue(filterRaw)) return null;
      pairs.push([idRaw, filterRaw]);
    }
    groups.push([groupKeyRaw, pairs]);
  }
  return groups;
}

function isFilterIdValue(id: unknown): id is FilterId {
  if (typeof id !== 'string') return false;
  for (const v of Object.values(FilterId)) {
    if (v === id) return true;
  }
  return false;
}

function isFilterValue(v: unknown): v is Filter {
  if (typeof v !== 'object' || v === null) return false;
  if (!('type' in v)) return false;
  const t = Reflect.get(v, 'type');
  if (typeof t !== 'string') return false;
  return Object.values(FilterType).some((ft) => ft === t);
}

const createFilterState = ({
  pageId: pageIdFromProps,
  pathname,
  filterPairs,
  taxonomyConfig,
  defaultSelections,
  primaryFilterId: pagePrimaryFilterId,
  primaryFilterSelectionLimit,
  showTaxonomyVersionToast,
}: {
  pageId: string;
  pathname: string;
  filterPairs: [FilterId, Filter][];
  taxonomyConfig?: TaxonomyConfig;
  defaultSelections?: DefaultSelectionOverride;
  primaryFilterId?: PrimaryFilterId;
  primaryFilterSelectionLimit?: number;
  showTaxonomyVersionToast?: (id: string, filterName: string) => void;
}) => {
  const primaryFilterId = filterPairs?.find(
    ([, f]: [FilterId, Filter]) => isTreeFilter(f) && f.isPrimary
  )?.[0] as PrimaryFilterId | undefined;

  const pageId = pageIdFromProps || pathname.split('/').pop() || '';

  const isScreenerV2 = getPrimaryFilterIdForScreenerV2Page(pageId);
  const consistencyKey = isScreenerV2
    ? pageId
    : (primaryFilterId ?? NO_PRIMARY_FILTER_KEY);

  const defaultFilterState = createInitialFilterConsistencyState(
    consistencyKey,
    taxonomyConfig,
    defaultSelections
  );
  const defaultFilterMap = getActiveFilterMap(
    defaultFilterState,
    taxonomyConfig
  );

  const taxonomyValidatedFilters = validateTaxonomyFilters({
    filters: filterPairs,
    taxonomyConfig: taxonomyConfig ?? {},
    showTaxonomyVersionToast,
  });

  const filterMap = validateParsedFilters(taxonomyValidatedFilters, {
    defaultFilterMap,
    primaryFilterId: pagePrimaryFilterId || primaryFilterId,
    primaryFilterLimit: primaryFilterSelectionLimit || 5,
  });

  const pageKey = generatePageKey(consistencyKey, pageId);

  return handleReplaceFilters(defaultFilterState, {
    skipReconciliation: true,
    filterMap,
    pageKey,
  });
};
